import React, { useEffect, useState } from "react";
import { Card, Modal, Button, Container, Spinner } from "react-bootstrap";
import * as FaIcons from "react-icons/fa";
import "./HelpTopics.css";
import { ShimmerPostList } from "react-shimmer-effects";
import LivevoxChat from "../pdp/pdpComponents/LivevoxChat";
import { getSupportData } from "@/api/faq";
import { trackButtonClick } from "@/utils/analytics";
import { useSelector } from "react-redux";
import { loginCmsDataSelector } from "@/redux/cms/cmsSelectors";

const ICONS = {
  search: <FaIcons.FaSearch size={30} className="text-primary" />,
  gavel: <FaIcons.FaGavel size={30} className="text-primary" />,
  edit: <FaIcons.FaPen size={30} className="text-primary" />,
  handshake: <FaIcons.FaHandshake size={30} className="text-primary" />,
  "user-plus": <FaIcons.FaUserPlus size={30} className="text-primary" />,
  envelope: <FaIcons.FaEnvelope size={30} className="text-primary" />,
  map: <FaIcons.FaMapMarkerAlt size={30} className="text-primary" />,
  filter: <FaIcons.FaFilter size={30} className="text-primary" />,
  save: <FaIcons.FaSave size={30} className="text-primary" />,
  heart: <FaIcons.FaHeart size={30} className="text-primary" />,
  list: <FaIcons.FaList size={30} className="text-primary" />,
  "user-check": <FaIcons.FaUserCheck size={30} className="text-primary" />,
  hammer: <FaIcons.FaHammer size={30} className="text-primary" />,
  "money-bill": <FaIcons.FaMoneyBill size={30} className="text-primary" />,
  clock: <FaIcons.FaClock size={30} className="text-primary" />,
  "money-check": <FaIcons.FaMoneyCheck size={30} className="text-primary" />,
  "check-circle": <FaIcons.FaCheckCircle size={30} className="text-primary" />,
  general: <FaIcons.FaInfoCircle size={30} className="text-primary" />,
  account: <FaIcons.FaUserCircle size={30} className="text-primary" />,
  billing: <FaIcons.FaCreditCard size={30} className="text-primary" />,
  technical: <FaIcons.FaCog size={30} className="text-primary" />,
  security: <FaIcons.FaShieldAlt size={30} className="text-primary" />,
  default: <FaIcons.FaQuestionCircle size={30} className="text-primary" />,
};

const HelpTopics = () => {
  const [showModal, setShowModal] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [selectedReason, setSelectedReason] = useState("");
  const [helpTopicsTitle, setHelpTopicsTitle] = useState("");
  const [helpTopicsDesc, setHelpTopicsDesc] = useState("");
  const [helpTopicsData, setHelpTopicsData] = useState(null);

  const loginCmsData = useSelector(loginCmsDataSelector);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleOpenModal = (topic) => {
    let parsedAnswer;

    // Only try to parse if the answer looks like JSON (starts with { or [)
    if (
      topic.answer.trim().startsWith("{") ||
      topic.answer.trim().startsWith("[")
    ) {
      try {
        const fixedJSON = topic.answer.replace(
          /([{,]\s*)([a-zA-Z0-9_]+)\s*:/g,
          '$1"$2":'
        );
        parsedAnswer = JSON.parse(fixedJSON);
      } catch (error) {
        console.error("Failed to parse answer JSON:", error);
        parsedAnswer = null;
      }
    }

    setSelectedTopic({
      ...topic,
      parsedAnswer,
    });
    setShowModal(true);
  };

  const handleFeedback = (isHelpful) => {
    setFeedback(isHelpful ? "helpful" : "not-helpful");
  };

  const handleChatCare = () => {
    // Add chat functionality
    trackButtonClick({
      eventName: "chat_with_customer_care",
      eventCategory: "Support-Page",
      eventSource: "Button Click",
      eventLabel: "Customer Care Chat",
    });
  };

  const handleSpeakCare = () => {
    setFeedback("speakWithCustomerCare");

    trackButtonClick({
      eventName: "speak_with_customer_care",
      eventCategory: "Support-Page",
      eventSource: "Button Click",
      eventLabel: "Customer Care Contact",
    });
  };

  const handleSearchAgain = () => {
    resetEverything(true);

    trackButtonClick({
      eventName: "search_again",
      eventCategory: "Support-Page",
      eventSource: "Button Click",
      eventLabel: "FAQ Search Reset",
    });
  };

  const resetEverything = (scrollToTop) => {
    setFeedback(null);
    setShowModal(false);
    setSelectedTopic(null);
    setSelectedReason("");
    scrollToTop && window.scrollTo(0, 0);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const result = await getSupportData("faqSection");
        const featuredHelpTopics = result.results[0]?.featuredHelpTopics;

        if (featuredHelpTopics) {
          setHelpTopicsTitle(featuredHelpTopics.title || "");
          setHelpTopicsDesc(featuredHelpTopics.description || "");

          const topics = Object.values(featuredHelpTopics.topics || {})
            .filter((topic) => topic && topic.question && topic.answer)
            .map((topic) => ({
              id: topic["@id"],
              question: topic.question,
              answer: topic.answer,
              link: topic.link,
            }));

          setHelpTopicsData(topics);
        }
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const renderSteps = (steps) => {
    return steps.map((step, index) => (
      <div key={index} className="ht-step-item">
        <div className="ht-step-icon">
          {ICONS[step.icon]}
          <div className="ht-step-connector"></div>
        </div>
        <div className="ht-step-content">
          <h6 className="ht-step-title">{step.title}</h6>
          <p className="ht-step-description">{step.description}</p>
        </div>
      </div>
    ));
  };

  const renderModalContent = () => {
    if (!selectedTopic) return null;

    return (
      <>
        {selectedTopic.image && (
          <div className="ht-image-container">
            <img
              src={selectedTopic.image}
              alt="Related visual"
              className="ht-modal-image"
            />
          </div>
        )}

        <div className="ht-answer-section">
          <div className="ht-section-header">
            <FaIcons.FaCommentDots className="ht-section-icon" />
            <h5>Answer</h5>
          </div>
          <div className="ht-answer-content">
            {typeof selectedTopic?.parsedAnswer === "object" &&
            selectedTopic.parsedAnswer.desktopImg1 ? (
              <div className="faq-responsive-image-wrapper">
                {/* Desktop View */}
                <div className="faq-desktop-images">
                  <img
                    src={selectedTopic.parsedAnswer.desktopImg1}
                    alt="Desktop Image 1"
                    className="img-fluid"
                  />
                  <img
                    src={selectedTopic?.parsedAnswer?.desktopImg2}
                    alt="Desktop Image 2"
                    className="img-fluid"
                  />
                </div>

                {/* Mobile View */}
                <div className="faq-mobile-images">
                  <img
                    src={selectedTopic?.parsedAnswer?.mobileView1}
                    alt="Mobile Image 1"
                    className="img-fluid"
                  />
                  <img
                    src={selectedTopic?.parsedAnswer?.mobileView2}
                    alt="Mobile Image 2"
                    className="img-fluid"
                  />
                </div>
              </div>
            ) : selectedTopic.answer.includes("https://") ? (
              <iframe
                width="100%"
                height="315"
                src={selectedTopic.answer}
                title={selectedTopic.question}
                allow="autoplay; encrypted-media"
                allowFullScreen
              ></iframe>
            ) : (
              selectedTopic.answer
            )}
          </div>
        </div>

        {selectedTopic.steps && (
          <div className="ht-steps-section">
            <div className="ht-section-header ht-steps-title">
              <FaIcons.FaListOl className="ht-section-icon" />
              <h5>Steps to Follow</h5>
            </div>
            <div className="ht-steps-grid">
              {renderSteps(selectedTopic.steps)}
            </div>
          </div>
        )}
      </>
    );
  };

  const renderFeedbackContent = () => {
    switch (feedback) {
      case "helpful":
        return (
          <div className="ht-feedback-success">
            <div className="success-icon">
              <FaIcons.FaCheckCircle />
            </div>
            <h5>Thank you for your feedback!</h5>
            <p>We're glad we could help you today.</p>
          </div>
        );
      case "not-helpful":
        return (
          <div className="ht-feedback-improvement">
            <h5>
              <FaIcons.FaCommentDots />
              What best describes the information we provided?
            </h5>
            <div className="ht-feedback-options">
              <div className="radio-option">
                <input
                  type="radio"
                  className="btn-check"
                  name="ht-feedback-reason"
                  id="confusing"
                  autoComplete="off"
                  onChange={() => setSelectedReason("confusing")}
                />
                <label htmlFor="confusing">
                  <FaIcons.FaPuzzlePiece />
                  <span>This information was confusing</span>
                </label>
              </div>
              <div className="radio-option">
                <input
                  type="radio"
                  className="btn-check"
                  name="ht-feedback-reason"
                  id="not-relevant"
                  autoComplete="off"
                  onChange={() => setSelectedReason("not-relevant")}
                />
                <label htmlFor="not-relevant">
                  <FaIcons.FaSearchMinus />
                  <span>This isn't the information I was looking for</span>
                </label>
              </div>
            </div>
            <div className="submit-section">
              <Button
                variant="primary"
                className="submit-btn"
                onClick={() => {
                  if (selectedReason) {
                    setFeedback("submitted");
                    console.log("Selected reason:", selectedReason);
                  }
                }}
                disabled={!selectedReason}
              >
                <FaIcons.FaPaperPlane />
                Submit Feedback
              </Button>
            </div>
          </div>
        );
      case "submitted":
        return (
          <div className="ht-feedback-submitted">
            <div className="submitted-header">
              <FaIcons.FaHeart />
              <h5>Thank you for your feedback!</h5>
              <p>We'll use this to improve our help content.</p>
            </div>
            <div className="ht-action-buttons">
              <Button
                variant="primary"
                aria-label="search"
                className="ht-action-btn ht-search-btn"
                onClick={handleSearchAgain}
              >
                <div className="ht-btn-content">
                  <FaIcons.FaSearch />
                  <span>Search Again</span>
                </div>
              </Button>
              <Button
                variant="success"
                aria-label="chat"
                className="ht-action-btn ht-chat-btn"
                onClick={handleChatCare}
              >
                <div className="ht-btn-content">
                  <FaIcons.FaCommentDots />
                  {/* <span>Chat With Customer Care</span> */}
                  <LivevoxChat
                    showContent={false}
                    contentText="Chat With Customer Care"
                    style={{ color: "#fff", marginLeft: "4px" }}
                  />
                </div>
              </Button>
              <Button
                variant="warning"
                aria-label="warning"
                className="ht-action-btn ht-call-btn"
                onClick={handleSpeakCare}
              >
                <div className="ht-btn-content">
                  <FaIcons.FaPhoneAlt />
                  <span>Speak With Customer Care</span>
                </div>
              </Button>
            </div>
          </div>
        );
      case "speakWithCustomerCare":
        return (
          <div className="success-action-container">
            <div className="success-message">
              <div className="message-icon">
                <FaIcons.FaCheckCircle />
              </div>
              <h5 className="fw-bold text-success mb-4 text-center">
                You can reach our Customer Care team at{" "}
                <a
                  href={`tel:${loginCmsData?.customerCareNumber}`}
                  className="customer-care"
                >
                  {loginCmsData?.customerCareNumber}
                </a>
                , Monday to Friday between the hours of 7 AM and 7 PM EDT.
              </h5>
            </div>
            <div className="ht-action-buttons-container">
              <Button
                variant="primary"
                className="ht-action-btn ht-search-btn"
                onClick={handleSearchAgain}
              >
                <div className="ht-btn-content">
                  <FaIcons.FaSearch />
                  <span>Search Again</span>
                </div>
              </Button>
              <Button
                variant="success"
                className="ht-action-btn ht-chat-btn"
                onClick={handleChatCare}
              >
                <div className="ht-btn-content">
                  <FaIcons.FaComments />
                  {/* <span>Chat With Customer Care</span> */}
                  <LivevoxChat
                    showContent={false}
                    contentText="Chat With Customer Care"
                    style={{ color: "#fff", marginLeft: "4px" }}
                  />
                </div>
              </Button>
            </div>
          </div>
        );
      default:
        return (
          <div className="ht-feedback-content">
            <div className="ht-feedback-question">
              <FaIcons.FaThumbsUp className="ht-feedback-icon" />
              <h5>Was this information helpful?</h5>
            </div>
            <div className="ht-feedback-buttons">
              <Button
                variant="outline-primary"
                className="ht-feedback-btn"
                onClick={() => handleFeedback(true)}
              >
                <FaIcons.FaCheck />
                <span>Yes, thanks!</span>
              </Button>
              <Button
                variant="outline-primary"
                className="ht-feedback-btn"
                onClick={() => handleFeedback(false)}
              >
                <FaIcons.FaTimes />
                <span>Not quite</span>
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="container my-5">
      {loading ? (
        <div className="help-topic__featured-section">
          <div className="help-topic__section-header">
            <h2 className="help-topic__main-title">
              Loading featured help topics...
            </h2>
            <div style={{ width: "100%" }}>
              <ShimmerPostList
                postStyle="STYLE_SEVEN"
                col={3}
                row={2}
                gap={30}
              />
            </div>
          </div>
        </div>
      ) : error ? (
        <div className="help-topic__featured-section">
          <div className="help-topic__section-header">
            <h2 className="help-topic__main-title">Error</h2>
            <p className="help-topic__subtitle">{error}</p>
          </div>
        </div>
      ) : (
        <>
          <div className="help-topic__featured-section">
            <div className="help-topic__section-header">
              {helpTopicsTitle ? (
                <h2 className="help-topic__main-title">{helpTopicsTitle}</h2>
              ) : (
                <h2 className="help-topic__main-title">Featured help topics</h2>
              )}
              {helpTopicsDesc && (
                <p className="help-topic__subtitle">{helpTopicsDesc}</p>
              )}
            </div>

            {helpTopicsData && helpTopicsData.length > 0 ? (
              <div className="help-topic__grid">
                {helpTopicsData.map((topic, index) => (
                  <div
                    key={index.toString()}
                    className="help-topic__card-wrapper"
                  >
                    <Card
                      className="help-topic__card"
                      onClick={() => handleOpenModal(topic)}
                    >
                      <Card.Body>
                        {/* <div className="help-topic__icon">
                          {ICONS[topic.icon] || ICONS.default}
                        </div> */}
                        <Card.Title className="help-topic__title">
                          {topic.question}
                        </Card.Title>
                        {/* <button type="button" className="help-topic__lmb">
                          <span>Learn More</span>
                          <FaIcons.FaArrowRight color="#fff" />
                        </button> */}
                      </Card.Body>
                    </Card>
                  </div>
                ))}
              </div>
            ) : (
              helpTopicsData &&
              helpTopicsData.length === 0 && (
                <Container className="help-topic__no-data text-center p-4 my-4 bg-light rounded-3 border border-2 border-dashed">
                  <div className="d-flex flex-column align-items-center gap-3">
                    <FaIcons.FaInbox className="fs-1 text-secondary opacity-75" />
                    <span className="fw-medium text-secondary">
                      No data available
                    </span>
                  </div>
                </Container>
              )
            )}
          </div>

          {/* Modal for the selected topic */}
          {selectedTopic && (
            <Modal
              show={showModal}
              onHide={() => resetEverything()}
              size="lg"
              centered
              className="ht-modal"
            >
              <Modal.Header closeButton className="ht-modal-header-custom">
                <Modal.Title className="ht-modal-title-custom-que">
                  <FaIcons.FaLightbulb />
                  {selectedTopic.question}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                {renderModalContent()}{" "}
                {/* This function will render content based on selectedTopic */}
                <div className="cta-section border-top">
                  {renderFeedbackContent()}{" "}
                  {/* This renders the feedback section for the modal */}
                </div>
              </Modal.Body>
            </Modal>
          )}
        </>
      )}
    </div>
  );
};

export default HelpTopics;
